import type { RefObject } from 'react';

import { useApplyPanDelta } from './useApplyPanDelta.js';
import { useApplyZoomDelta } from './useApplyZoomDelta.js';
import { useResetInteractions } from './useResetInteractions.js';
import { useZoomToSelection } from './useZoomToSelection.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import {
  INTERACTION_MODE,
  type InteractionMode,
  type InteractionTriggers,
  isSelectCancelled,
  type ZoomBehavior,
} from '../../../../core/components/interactions-overlay/types.js';
import { useTooltipActions } from '../../../../core/components/overlay-tooltip/useTooltipActions.js';
import type {
  _SelectionRef,
  SelectionAreaVariant,
} from '../../../../core/components/selection-area/types.js';
import { useSynchronizedCrosshair } from '../../../../core/components/shared-chart-interactions/hooks/useSynchronizedCrosshair.js';
import { useSynchronizedSelection } from '../../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import type { RelativeAbsolutePosition } from '../../../../core/types/pointer-events.js';
import { ZOOM_CHANGE_TYPE } from '../../../../core/types/zoom.js';
import { getPercentageFromPixel } from '../../../../core/utils/get-percentage-from-pixel.js';
import { isTouchableDevice } from '../../../../core/utils/is-touchable-device.js';
import { useAreaData } from '../../../hooks/useAreaData.js';
import { useBarData } from '../../../hooks/useBarData.js';
import { useDotData } from '../../../hooks/useDotData.js';
import { useLineData } from '../../../hooks/useLineData.js';
import { useOverlayPerformance } from '../../../hooks/useOverlayPerformance.js';
import { useRectData } from '../../../hooks/useRectData.js';
import { useSyncedXScaleByPriority } from '../../../hooks/useSyncedXScaleByPriority.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartId } from '../../../hooks/useXYChartId.js';
import { reduceInteractionMode } from '../../../store/reduce-interaction-mode.js';
import {
  reduceExploreCancelAll,
  reduceExploreCancelHighlight,
  reduceExploreCancelSelect,
  reduceExplorePin,
  reduceExploreReset,
  reduceExploreSelect,
  reduceExploreUnPin,
} from '../../../store/reducer-explorer.js';
import {
  useInteractionModeState,
  useSetState,
  useZoomState,
} from '../../../store/selectors.js';

export const useOverlayEvents = () => {
  const { filteredSeriesWithDatapoints: rectSeriesData } = useRectData();
  const { filteredSeriesWithDatapoints: barSeriesData } = useBarData();
  const { filteredSeriesWithDatapoints: dotSeriesData } = useDotData();
  const { filteredSeriesWithDatapoints: lineSeriesData } = useLineData();
  const { filteredSeriesWithDatapoints: areaSeriesData } = useAreaData();
  const zoomToSelection = useZoomToSelection();
  const setState = useSetState();
  const mode = useInteractionModeState();
  const applyPanDelta = useApplyPanDelta();
  const applyReset = useResetInteractions();
  const applyZoomDelta = useApplyZoomDelta();
  const zoomState = useZoomState();
  const { query } = useOverlayPerformance();
  const { axesMetadata } = useXYChartData();
  const chartId = useXYChartId();
  const synchronizedCrosshair = useSynchronizedCrosshair();
  const { width } = useGridPlotSize();
  const {
    updateSelectionState: updateSynchronizedSelection,
    resetSelectionState: resetSynchronizedSelection,
  } = useSynchronizedSelection();
  const syncedXScale = useSyncedXScaleByPriority();
  const { hover, pin, unpin, reset, outOfBounds } = useTooltipActions();
  const {
    updateHoverState: updateSynchronizedCrosshair,
    setDisabled: disableSynchronizedCrosshair,
  } = synchronizedCrosshair ?? {};

  const getDatapoints = (position: RelativeAbsolutePosition) =>
    query({
      position,
      dotSeriesData,
      lineSeriesData,
      areaSeriesData,
      rectSeriesData,
      barSeriesData,
    });

  const resetTooltip = () => {
    setState(reduceExploreCancelHighlight);
    reset();
  };

  const handleModeChange = (event: {
    mode: InteractionMode;
    keyboard: boolean;
  }) => {
    const canPan = axesMetadata.isPanEnabled && zoomState.zoomOutEnabled;
    const shouldSwitchToPanMode = event.mode === 'pan' && canPan;
    const shouldSwitchToZoomMode =
      event.mode === 'zoom' && axesMetadata.isZoomEnabled;

    if (shouldSwitchToPanMode || shouldSwitchToZoomMode) {
      resetSynchronizedSelection?.();
      setState(reduceInteractionMode(event.mode));
      setState(reduceExploreReset);
    } else if (event.mode === 'explore') {
      setState(reduceInteractionMode(INTERACTION_MODE.Explore));
    }
  };

  const handleHover = ({
    position,
  }: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition;
  }) => {
    if (isTouchableDevice()) {
      return;
    }

    if (syncedXScale) {
      const hoveredDate = syncedXScale.invert(position.relative.x);
      disableSynchronizedCrosshair?.(false);
      updateSynchronizedCrosshair?.(chartId, hoveredDate.valueOf());
    }

    const metadata = getDatapoints(position);

    const payload = metadata.preferred
      ? {
          geometry: metadata.preferred.type,
          header1: undefined,
          series: [],
          metadata,
        }
      : undefined;

    hover(
      { clientX: position.absolute.x, clientY: position.absolute.y },
      payload,
    );
  };

  const handleTogglePin = (
    shouldPin: boolean,
    position: RelativeAbsolutePosition | undefined,
  ) => {
    if (shouldPin) {
      handlePin(position);
      updateSynchronizedCrosshair?.(undefined);
    } else {
      handleUnPin(position);
    }
  };

  const handleUnPin = (position: RelativeAbsolutePosition | undefined) => {
    setState(reduceExploreUnPin);

    if (position) {
      const metadata = getDatapoints(position);

      unpin();

      if (metadata.preferred) {
        hover(
          { clientX: position.absolute.x, clientY: position.absolute.y },
          {
            geometry: metadata.preferred.type,
            header1: undefined,
            series: [],
            metadata,
          },
        );
      }
    } else {
      resetTooltip();
    }
  };

  const handlePin = (position: RelativeAbsolutePosition | undefined) => {
    if (!position) {
      return;
    }

    const metadata = getDatapoints(position);

    if (metadata.preferred) {
      setState(reduceExplorePin(metadata.preferred.series.id));
      pin(
        { clientX: position.absolute.x, clientY: position.absolute.y },
        {
          geometry: metadata.preferred.type,
          header1: undefined,
          series: [],
          metadata,
        },
      );
    }
  };

  const handleReset = (event: {
    triggeredBy: InteractionTriggers;
    mousePosition?: RelativeAbsolutePosition;
  }) => {
    const { mousePosition: position } = event;
    applyReset({ position });
    disableSynchronizedCrosshair?.(false);
    resetSynchronizedSelection?.();

    if (!position) {
      setState(reduceExploreCancelAll);

      return;
    }

    setState(reduceExploreCancelAll);

    const metadata = getDatapoints(position);

    if (metadata.preferred) {
      hover(
        { clientX: position.absolute.x, clientY: position.absolute.y },
        {
          geometry: metadata.preferred.type,
          header1: undefined,
          series: [],
          metadata,
        },
      );
    } else {
      outOfBounds();
    }
  };

  const handleLeave = (_: { triggeredBy: InteractionTriggers }) => {
    updateSynchronizedCrosshair?.(undefined);
    outOfBounds();
  };

  const handlePanChange = (event: {
    triggeredBy: InteractionTriggers;
    delta: number;
  }) => {
    applyPanDelta(event.delta);
  };

  const handleSelectStart = (event: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition;
  }) => {
    if (mode === 'explore') {
      disableSynchronizedCrosshair?.(true);
      updateSynchronizedSelection?.();
    }
    resetTooltip();
  };

  const handleSelectChange = (event: {
    triggeredBy: InteractionTriggers;
    start: RelativeAbsolutePosition;
    end: RelativeAbsolutePosition;
    mode: 'explore' | 'zoom';
  }): void => {
    const variant: SelectionAreaVariant =
      event.mode === 'explore' ? 'select' : 'zoom';

    setState(
      reduceExploreSelect({
        start: event.start,
        end: event.end,
        finished: false,
        accessible: false,
        variant,
        selectionPercentage: getPercentageFromPixel(
          [event.start, event.end],
          width,
        ),
      }),
    );
    if (mode === 'explore' && syncedXScale) {
      updateSynchronizedSelection?.({
        selection: [event.start, event.end],
        chartId,
        scale: syncedXScale,
      });
    }
  };

  const handleSelectEnd = (
    event:
      | {
          triggeredBy: InteractionTriggers;
          start: RelativeAbsolutePosition;
          end: RelativeAbsolutePosition;
        }
      | { cancelled: true },
    ref: RefObject<_SelectionRef>,
  ): void => {
    if (isSelectCancelled(event)) {
      setState(reduceExploreCancelSelect);
      resetSynchronizedSelection?.();
    } else if (mode === 'explore') {
      setState(
        reduceExploreSelect({
          start: event.start,
          end: event.end,
          finished: true,
          accessible: false,
          variant: 'select',
          selectionPercentage: getPercentageFromPixel(
            [event.start, event.end],
            width,
          ),
        }),
      );
      ref.current?.focus();
      if (syncedXScale) {
        updateSynchronizedSelection?.({
          selection: [event.start, event.end],
          chartId,
          scale: syncedXScale,
        });
      }
    } else {
      // in case select is disabled and zoomX is enabled, only zoom to selection interaction is possible
      zoomToSelection();
    }
  };

  const handleZoomChange = (event: {
    triggeredBy: InteractionTriggers;
    deltaStart: number;
    deltaEnd: number;
    behavior: ZoomBehavior;
  }) => {
    const canZoomIn =
      event.behavior === ZOOM_CHANGE_TYPE.ZoomIn && zoomState?.zoomInEnabled;
    const canZoomOut =
      event.behavior === ZOOM_CHANGE_TYPE.ZoomOut && zoomState.zoomOutEnabled;
    const canZoomX = event.behavior === ZOOM_CHANGE_TYPE.ZoomX;

    const canZoom = canZoomIn || canZoomOut || canZoomX;

    if (canZoom && axesMetadata.isZoomEnabled) {
      setState(reduceExploreCancelAll);

      applyZoomDelta(event.deltaStart, event.deltaEnd, event.behavior);
    }
  };

  return {
    handleModeChange,
    handleHover,
    handleTogglePin,
    handleReset,
    handleLeave,
    handlePanChange,
    handleSelectChange,
    handleSelectEnd,
    handleZoomChange,
    handleSelectStart,
    resetTooltip,
  };
};
